/**
 * Log Service - Centralized logging system
 *
 * Captures and stores all system logs including:
 * - Application events
 * - API calls
 * - Errors and exceptions
 * - User actions
 * - Console messages
 *
 * Logs are stored in localStorage and can be exported for debugging.
 */

import { StorageService } from './storage';
import type { Log, LogLevel } from '../types';

interface LogServiceConfig {
  maxLogs: number;
  captureConsole: boolean;
  captureErrors: boolean;
}

class LogServiceClass {
  private config: LogServiceConfig = {
    maxLogs: 1000,
    captureConsole: true,
    captureErrors: true,
  };

  private logIdCounter = 0;
  private originalConsole = {
    log: console.log,
    warn: console.warn,
    error: console.error,
    info: console.info,
  };

  /**
   * Initialize the log service
   */
  initialize(config?: Partial<LogServiceConfig>) {
    this.config = { ...this.config, ...config };

    // Capture console messages
    if (this.config.captureConsole) {
      this.interceptConsole();
    }

    // Capture unhandled errors
    if (this.config.captureErrors) {
      this.interceptErrors();
    }

    this.log('INFO', 'System', 'LogService initialized');
  }

  /**
   * Log a message
   */
  log(level: LogLevel, category: string, message: string, data?: unknown): void {
    const logs = this.getLogs();
    const newLog: Log = {
      id: String(++this.logIdCounter),
      level,
      category,
      message,
      data,
      timestamp: Date.now(),
    };

    logs.push(newLog);

    // Keep only last maxLogs
    if (logs.length > this.config.maxLogs) {
      logs.splice(0, logs.length - this.config.maxLogs);
    }

    StorageService.set('system_logs', logs);

    // Also log to console (using original methods to avoid recursion)
    const consoleMethod = level === 'ERROR' || level === 'CRITICAL' ? this.originalConsole.error :
                         level === 'WARNING' ? this.originalConsole.warn :
                         this.originalConsole.info;

    consoleMethod(`[${level}] [${category}] ${message}`, data || '');
  }

  /**
   * Convenience methods for different log levels
   */
  debug(category: string, message: string, data?: unknown): void {
    this.log('DEBUG', category, message, data);
  }

  info(category: string, message: string, data?: unknown): void {
    this.log('INFO', category, message, data);
  }

  success(category: string, message: string, data?: unknown): void {
    this.log('SUCCESS', category, message, data);
  }

  warn(category: string, message: string, data?: unknown): void {
    this.log('WARNING', category, message, data);
  }

  error(category: string, message: string, data?: unknown): void {
    this.log('ERROR', category, message, data);
  }

  critical(category: string, message: string, data?: unknown): void {
    this.log('CRITICAL', category, message, data);
  }

  /**
   * Get all logs
   */
  getLogs(): Log[] {
    return StorageService.get<Log[]>('system_logs', []);
  }

  /**
   * Clear all logs
   */
  clearLogs(): void {
    StorageService.set('system_logs', []);
  }

  /**
   * Intercept console methods
   */
  private interceptConsole(): void {
    console.log = (...args) => {
      this.originalConsole.log(...args);
      this.log('DEBUG', 'Console', String(args[0]), args.slice(1));
    };

    console.info = (...args) => {
      this.originalConsole.info(...args);
      this.log('INFO', 'Console', String(args[0]), args.slice(1));
    };

    console.warn = (...args) => {
      this.originalConsole.warn(...args);
      this.log('WARNING', 'Console', String(args[0]), args.slice(1));
    };

    console.error = (...args) => {
      this.originalConsole.error(...args);
      this.log('ERROR', 'Console', String(args[0]), args.slice(1));
    };
  }

  /**
   * Intercept global errors
   */
  private interceptErrors(): void {
    // Capture unhandled errors
    window.addEventListener('error', (event) => {
      this.log('ERROR', 'Global', event.message, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error?.stack || event.error?.message
      });
    });

    // Capture unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.log('ERROR', 'Promise', `Unhandled rejection: ${event.reason}`, {
        reason: event.reason,
        promise: String(event.promise)
      });
    });
  }

  /**
   * Generate an error report for sharing
   */
  generateErrorReport(): string {
    const logs = this.getLogs();
    const errors = logs.filter(l => l.level === 'ERROR' || l.level === 'CRITICAL');
    const warnings = logs.filter(l => l.level === 'WARNING');

    const report = [
      '═══════════════════════════════════════════════',
      '       WEALTHHUB CRYPTO - ERROR REPORT',
      '═══════════════════════════════════════════════',
      '',
      '📊 SYSTEM INFORMATION',
      '───────────────────────────────────────────────',
      `Date: ${new Date().toLocaleString('fr-FR')}`,
      `User Agent: ${navigator.userAgent}`,
      `Platform: ${navigator.platform}`,
      `Language: ${navigator.language}`,
      `Screen: ${window.screen.width}x${window.screen.height}`,
      `Viewport: ${window.innerWidth}x${window.innerHeight}`,
      `Online: ${navigator.onLine ? 'Yes' : 'No'}`,
      `Cookie Enabled: ${navigator.cookieEnabled ? 'Yes' : 'No'}`,
      '',
      '📈 LOGS SUMMARY',
      '───────────────────────────────────────────────',
      `Total Logs: ${logs.length}`,
      `Errors: ${errors.length}`,
      `Warnings: ${warnings.length}`,
      '',
    ];

    // Add recent errors
    if (errors.length > 0) {
      report.push('🚨 RECENT ERRORS (Last 10)');
      report.push('───────────────────────────────────────────────');
      errors.slice(-10).reverse().forEach((log, index) => {
        report.push(`\n[${index + 1}] ${new Date(log.timestamp).toLocaleString('fr-FR')}`);
        report.push(`    Category: ${log.category}`);
        report.push(`    Message: ${log.message}`);
        if (log.data) {
          report.push(`    Data: ${JSON.stringify(log.data, null, 2).split('\n').map(l => '    ' + l).join('\n').trim()}`);
        }
      });
      report.push('');
    }

    // Add recent warnings
    if (warnings.length > 0) {
      report.push('⚠️  RECENT WARNINGS (Last 5)');
      report.push('───────────────────────────────────────────────');
      warnings.slice(-5).reverse().forEach((log, index) => {
        report.push(`\n[${index + 1}] ${new Date(log.timestamp).toLocaleString('fr-FR')}`);
        report.push(`    Category: ${log.category}`);
        report.push(`    Message: ${log.message}`);
      });
      report.push('');
    }

    // Add all recent logs (last 20)
    report.push('📝 RECENT ACTIVITY (Last 20 logs)');
    report.push('───────────────────────────────────────────────');
    logs.slice(-20).reverse().forEach((log) => {
      const emoji = log.level === 'ERROR' || log.level === 'CRITICAL' ? '❌' :
                    log.level === 'WARNING' ? '⚠️' :
                    log.level === 'SUCCESS' ? '✅' :
                    log.level === 'INFO' ? 'ℹ️' : '🔍';
      report.push(`${emoji} [${new Date(log.timestamp).toLocaleTimeString('fr-FR')}] [${log.category}] ${log.message}`);
    });

    report.push('');
    report.push('═══════════════════════════════════════════════');
    report.push('        END OF REPORT');
    report.push('═══════════════════════════════════════════════');

    return report.join('\n');
  }

  /**
   * Copy error report to clipboard
   */
  async copyErrorReport(): Promise<boolean> {
    const report = this.generateErrorReport();
    try {
      await navigator.clipboard.writeText(report);
      return true;
    } catch (e) {
      this.originalConsole.error('Failed to copy error report:', e);
      return false;
    }
  }

  /**
   * Download error report as file
   */
  downloadErrorReport(): void {
    const report = this.generateErrorReport();
    const blob = new Blob([report], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wealthhub-error-report-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }
}

// Export singleton instance
export const LogService = new LogServiceClass();
